<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div class="cart-drawer critical-component-hide">
    <div
      class="cart-drawer__content-wrapper"
      :class="{ 'empty-cart': cart.item_count === 0 }"
      js-overlay="scrollElement"
    >
      <div class="cart-drawer__content">
        <div class="cart-drawer__title-wrapper">
          <h1
            class="cart-drawer__title font-family-freight"
            v-html="
              cart.item_count > 0 ?
                $string('cart.accessibility.title_html', {replace: { count: cart.item_count }, }) :
                $string('cart.title_empty')"
          />

          <div
            v-if="cart.item_count > 0 && $variable('settings.cart.specialtyMessage')"
            class="cart-drawer__specialty-message text-body-2xs-regular"
            v-html="$localizeRichtext($variable('settings.cart.specialtyMessage'))"
          />

          <divider-line
            v-if="!cart.item_count"
            class="cart-drawer__divider"
          />
        </div>

        <form
          v-if="cart.item_count > 0 || cartStatus.label === 'adding'"
          class="cart-drawer__form"
          :action="`${$variable('routes.cart.url')}?locale=${$variable('store.language')}`"
          method="post"
        >
          <div
            class="cart-drawer__line-items-scroll"
            js-overlay="scrollElement"
          >
            <div class="cart-drawer__line-items-wrapper">
              <div
                class="cart-drawer__line-items"
                js-overlay="scrollElement"
              >
                <product-matching-set
                  modifier="block"
                  :product="cart?.items[0]?.handle"
                  placement="mini-cart"
                />

                <template
                  v-for="(lineItem) of cartItems"
                  :key="getCartItemKey(lineItem)"
                >
                  <line-item
                    class="cart-drawer__line-item"
                    :cart-items="cartItems"
                    :line-item="lineItem"
                    small
                  />
                </template>
              </div>
            </div>

          </div>

          <div class="cart-summary__container">

            <cart-summary
              v-if="cart.item_count > 0"
              class="cart-drawer__footer"
              small
            />
          </div>
        </form>

        <template v-else>
          <div class="cart-drawer__empty">
            <p
              class=" cart-drawer__empty-message text-body-2xs-regular"
              v-text="$string('cart.empty.text_html')"
            />

            <btn
              v-if="
                $variable('settings.cart.emptyCartButton.label') &&
                  $variable('settings.cart.emptyCartButton.url')
              "
              class="cart-drawer__empty-cta"
              :label="$variable('settings.cart.emptyCartButton.label')"
              :url="$localizeUrl($variable('settings.cart.emptyCartButton.url'))"
              modifiers="outline center"
            />
          </div>
        </template>
        <div class="cart-drawer__recommendations" />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Cart drawer (cart-drawer)
 * -----------------------------------------------------------------------------
 * Displays contents of cart.
 *
 * @param {Object} overlay - Overlay API.
 * @param {Object} section - Shopify section settings.
 *
 */
import { mapState } from 'vuex'

import Btn from '~global/btn/btn'

import CartSummary from '~async/cart-summary/cart-summary'
import LineItem from '~async/line-item/line-item'
import DividerLine from '~async/utils/divider-line/divider-line'
import ProductMatchingSet from '~async/product-matching-set/product-matching-set'

export default {
  name: 'CartDrawer',

  components: {
    CartSummary,
    LineItem,
    DividerLine,
    Btn,
    ProductMatchingSet,
  },

  props: {
    overlay: {
      type: Object,
      default: () => ({}),
    },
    section: {
      type: Object,
      default: () => ({
        dynamicYieldTarget: null,
      }),
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      dyCampaigns: (state) => state['dynamic-yield'].campaigns,
      cart: (state) => state.cart.response,
      cartStatus: (state) => state.cart.status,
    }),

    /**
     * Compute cart items.
     * - For each item that is being added, check if it's already in the cart.
     * - Check against key (if adding from cart) or id (if adding from other).
     * - If it isn't then add a false item to act as a loading placeholder.
     * @returns {Array}
     */
    cartItems() {
      if (this.cartStatus.label !== 'adding') {
        return this.cart.items
      }

      const placeholders = []

      this.cartStatus.items.forEach((addedItem) => {
        const match = this.cart.items.find((cartItem) => {
          const cartItemProperty = typeof addedItem === 'string'
            ? cartItem.key
            : cartItem.id

          return cartItemProperty === addedItem
        })

        if (match) {
          return
        }

        placeholders.push(false)
      })

      return [
        ...placeholders,
        ...this.cart.items,
      ]
    },

    /**
     * The DY campaign for this section, including Shopify section data.
     * @returns {Object} The DY campaign data with Shopify section data.
     */
    dyCampaign() {
      if (!this.section.dynamicYieldTarget) {
        return null
      }

      const dyCampaign = this.dyCampaigns?.[this.section.dynamicYieldTarget]

      if (!dyCampaign) {
        return null
      }

      return {
        ...dyCampaign,
        section: this.section,
      }
    },
  },

  watch: {

    /**
     * Watch cart items to update focus trap.
     */
    'cart.items'() {
      if (!this.overlay.isActive) {
        return
      }

      this.overlay.update({ currentElement: document.activeElement })
    },
  },

  async mounted() {
    this.overlay.update()

    await this.$nextTick()
  },

  methods: {

    /**
     * Get cart item key.
     * @param {Object} item - Line item.
     * @param {Number} index - Line item index.
     * @returns {String}
     */
    getCartItemKey(item, index) {
      if (!item) {
        return `cart-line-item-loading-${index}`
      }

      return `cart-line-item-${item.key}`
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      window.cnvs?.EventBus?.emit('overlay:open-cart-drawer')
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.overlay.close()
    },
  },
}
</script>
